import React, {FC, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ExtractProps} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  AsyncMoreStatusesEnum,
  AsyncStatusesEnum,
} from '../../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {ScrollDirectionsEnum} from '../../../../../../libs/shared/core/src/helpers/types/scrollerTypes';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {AsyncLoader} from '../../../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {Loader, LoaderVariantsEnum} from '../../../../../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {center, ellipsis} from '../../../../../../libs/shared/ui/src/styles/mixins';
import {BasicActivityTypesEnum, CommentActivityTypesEnum} from '../../../../../models/api/activityModel';
import {LegacyNotificationModel} from '../../../../../models/api/legacyNotificationModel';
import {InboxesFiltersEnum} from '../../../../../models/url/models/inboxesUrlModel';
import {VariableHeightScroller} from '../../../../common/scroll/variableHeightScroller';
import {InfiniteScrollerProps} from '../../../../common/scroll/variableHeightScrollerTypes';
import {SegmentedFilters} from '../../../../common/segmentedFilters/segmentedFilters';
import {ArchiveNotificationCell} from './archiveNotificationCell';
import {CommentNotificationCell} from './commentNotificationCell';
import {MentionNotificationCellContainer} from './mentionNotificationCellContainer';
import {RuleNotificationCell} from './ruleNotificationCell';

/*
 * Props.
 */

interface NotificationCenterDropdownProps extends ExtractProps<typeof Dropdown> {
  status: AsyncStatusesEnum;
  statusMore: AsyncMoreStatusesEnum;
  notifications: ReadonlyArray<LegacyNotificationModel>;
  fetchMore: () => void;
  selectedFilter: InboxesFiltersEnum;
  canMarkAllAsRead: boolean;
  onFilterClick: (arg: InboxesFiltersEnum) => void;
  onMarkAllAsReadClick: () => void;
  onToggleMarkAsReadClick: (model: LegacyNotificationModel) => void;
  onRetryClick: () => void;
}

/*
 * Style.
 */

const StyledDropdown = styled(Dropdown)`
  margin-top: 4px;
`;

const StyledDropdownDiv = styled.div`
  height: 100%;

  display: grid;
  grid-template-rows: auto 1fr;
`;

const StyledHeaderDiv = styled.div`
  grid-row: 1;
  grid-column: 1;
  padding: 12px;
  border-bottom: 1px solid ${(p) => p.theme.greys.shade30};

  display: grid;
  grid-template-columns: 1fr auto 1fr;
  grid-template-rows: auto auto;
  grid-column-gap: 6px;
  grid-row-gap: 12px;
  grid-template-areas:
    'top-left title   top-right'
    'filters  filters filters';

  color: ${(p) => p.theme.greys.shade80};
  text-align: center;
  font-weight: ${fontWeights.semibold};
`;

const StyledTitleDiv = styled.div`
  grid-area: title;
`;

const StyledButtonDiv = styled.div`
  grid-area: top-right;
  justify-self: right;
  margin: -5px;
`;

const StyledFiltersDiv = styled.div`
  grid-area: filters;
`;

const StyledPlaceholderDiv = styled.div`
  grid-row: 1 / span 2;
  grid-column: 1;
  margin-top: 20px;
  ${center()};
`;

const StyledEmptyDiv = styled.div`
  padding: 20px;

  text-align: center;
  color: ${(p) => p.theme.greys.shade50};
  font-size: ${fontSizes.large};
  font-weight: ${fontWeights.semibold};
  ${ellipsis()};
`;

const StyledLoaderDiv = styled.div`
  height: 40px;
  ${center()};
`;

/*
 * Component.
 */

export const NotificationCenterDropdown: FC<NotificationCenterDropdownProps> = (props) => (
  <StyledDropdown {...props} width={400} height={500}>
    <StyledDropdownDiv>
      {renderContent(props)}
      <StyledHeaderDiv>
        <StyledTitleDiv>
          <Trans id="nc-title">Notifications</Trans>
        </StyledTitleDiv>
        {maybeRenderMarkAllAsRead(props)}
        <StyledFiltersDiv>{renderFilters(props)}</StyledFiltersDiv>
      </StyledHeaderDiv>
    </StyledDropdownDiv>
  </StyledDropdown>
);

/*
 * Header.
 */

function maybeRenderMarkAllAsRead(props: NotificationCenterDropdownProps) {
  if (!props.canMarkAllAsRead) {
    return null;
  }

  return (
    <StyledButtonDiv>
      <Button
        size={VisualSizesEnum.SMALL}
        variant={ButtonVariantsEnum.LINK}
        onClick={props.onMarkAllAsReadClick}
      >
        <Trans id="nc-readall">Mark all read</Trans>
      </Button>
    </StyledButtonDiv>
  );
}

const notificationsFilters = [InboxesFiltersEnum.OPEN, InboxesFiltersEnum.ALL];
const filterNames: {[T in InboxesFiltersEnum]?: ReactNode} = {
  [InboxesFiltersEnum.OPEN]: <Trans id="nc-filter-unread">Unread</Trans>,
  [InboxesFiltersEnum.ALL]: <Trans id="nc-filter-all">All</Trans>,
};

function renderFilters(props: NotificationCenterDropdownProps) {
  const {selectedFilter, onFilterClick} = props;

  const selectedFilterIndex = notificationsFilters.indexOf(selectedFilter);
  const filterDefinitions = notificationsFilters.map((filter) => ({
    id: filter,
    content: filterNames[filter],
    onClick: () => onFilterClick(filter),
  }));

  return <SegmentedFilters selectedFilterIndex={selectedFilterIndex} filters={filterDefinitions} />;
}

/*
 * Content.
 */

function renderContent(props: NotificationCenterDropdownProps) {
  const {status, notifications, onRetryClick} = props;

  if (status !== AsyncStatusesEnum.LOADED) {
    return (
      <StyledPlaceholderDiv>
        <AsyncLoader status={status} onRetryClick={onRetryClick} />
      </StyledPlaceholderDiv>
    );
  }

  if (notifications.length === 0) {
    return (
      <StyledPlaceholderDiv>
        <StyledEmptyDiv>
          <Trans id="nc-empty">No notifications</Trans>
        </StyledEmptyDiv>
      </StyledPlaceholderDiv>
    );
  }

  const {statusMore, fetchMore} = props;
  const infinite: InfiniteScrollerProps = {
    isLoadingMoreTop: false,
    hasMoreTop: false,
    hasMoreBottom: statusMore === AsyncMoreStatusesEnum.HAS_MORE,
    loadMoreThreshold: 100,
    loadMore: fetchMore,
    loaderRenderer: (direction) => renderLoader(statusMore, direction),
  };

  return <VariableHeightScroller infinite={infinite}>{renderNotifications(props)}</VariableHeightScroller>;
}

function renderNotifications(props: NotificationCenterDropdownProps) {
  return props.notifications.map((n) => renderNotification(props, n));
}

function renderNotification(props: NotificationCenterDropdownProps, notification: LegacyNotificationModel) {
  switch (notification.type) {
    case CommentActivityTypesEnum.MENTION:
      return (
        <MentionNotificationCellContainer
          key={notification.id}
          notification={notification}
          onRequestClose={props.onRequestClose}
          onToggleMarkAsReadClick={() => props.onToggleMarkAsReadClick(notification)}
        />
      );

    case CommentActivityTypesEnum.COMMENT:
      return (
        <CommentNotificationCell
          key={notification.id}
          notification={notification}
          onRequestClose={props.onRequestClose}
          onToggleMarkAsReadClick={() => props.onToggleMarkAsReadClick(notification)}
        />
      );

    case BasicActivityTypesEnum.RULE_NOTIFY:
      return (
        <RuleNotificationCell
          key={notification.id}
          notification={notification}
          onRequestClose={props.onRequestClose}
          onToggleMarkAsReadClick={() => props.onToggleMarkAsReadClick(notification)}
        />
      );

    case BasicActivityTypesEnum.ARCHIVE:
      return (
        <ArchiveNotificationCell
          key={notification.id}
          notification={notification}
          onRequestClose={props.onRequestClose}
          onToggleMarkAsReadClick={() => props.onToggleMarkAsReadClick(notification)}
        />
      );

    default:
      return [];
  }
}

function renderLoader(statusMore: AsyncMoreStatusesEnum, direction: ScrollDirectionsEnum) {
  if (direction === ScrollDirectionsEnum.TOP) {
    return null;
  }

  if (statusMore === AsyncMoreStatusesEnum.HAS_MORE) {
    return (
      <StyledLoaderDiv>
        <Loader size={VisualSizesEnum.MEDIUM} variant={LoaderVariantsEnum.BASE} isEnabled={false} />
      </StyledLoaderDiv>
    );
  }

  if (statusMore === AsyncMoreStatusesEnum.LOADING) {
    return (
      <StyledLoaderDiv>
        <Loader size={VisualSizesEnum.MEDIUM} variant={LoaderVariantsEnum.BASE} />
      </StyledLoaderDiv>
    );
  }

  return null;
}
